<template>
  <div class="mb-15">
    <div class="d-flex">
      <page-title :value="$t('foodMenu.title')" />
      <v-spacer />
      <v-menu offset-y>
        <template v-slot:activator="{ props }">
          <v-btn
            color="primary"
            variant="outlined"
            v-bind="props"
          >
            <v-icon start>mdi-tray-arrow-down</v-icon>
            {{ $t('common.download') }}
          </v-btn>
        </template>
        <v-list>
          <v-list-item @click="downloadSample">
            <v-list-item-title>{{ $t('common.sampleCsv') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div class="my-8">
      <v-card
        class="pa-4"
        color="#f0f0f0"
        variant="flat"
      >
        <div class="d-flex mb-4 align-center">
          <h3 class="mr-2">{{ $t('foodMenu.todayMenu', { date: todayLabel }) }}</h3>
          <v-btn
            variant="flat"
            color="primary"
            @click="setCreating(todayCode)"
          >{{ $t('common.add') }}</v-btn>
        </div>
        <div v-if="todaysMenu && todaysMenu.menus.length > 0">
          <div class="pa-2" style="background-color: white;">
            <div class="menu-table-row-col-items">
              <div 
                class="menu-table-row-col-item"
                v-for="(menu, i) in todaysMenu.menus"
                :key="i"
              >
                <div class="menu-table-row-col col-category">
                  {{ menu.category.name }}
                </div>
                <div class="menu-table-row-col col-menu">
                  {{ menu.body }}
                </div>
                <div class="menu-table-row-col col-classes">
                  {{ formatClassNames(menu.category.targetClasses) }}
                </div>
                <div class="menu-table-row-col col-actions">
                  <v-btn
                    color="primary"
                    variant="flat"
                    rounded
                    class="mx-1"
                    small
                    @click="openTestDialog(menu)"
                  >
                    <span v-if="menu.test">{{ $t('foodMenu.inspected') }}</span>
                    <span v-else>{{ $t('foodMenu.inspect') }}</span>
                  </v-btn>
                  <v-btn
                    color="primary"
                    variant="outlined"
                    rounded
                    class="mx-1"
                    small
                    @click="openEditDialog(menu)"
                  >
                    {{ $t('common.edit') }}
                  </v-btn>
                  <v-btn
                    color="red"
                    variant="outlined"
                    rounded
                    class="mx-1"
                    small
                    @click="deleteMenu(menu)"
                  >
                    {{ $t('common.delete') }}
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else>{{ $t('foodMenu.noMenuToday') }}</div>
      </v-card>
    </div>

    <div class="my-4">
      <month-pager v-model="yearMonth"
        @input="pageClick"
      />
    </div>
    <div class="my-4">
      <v-btn
        variant="flat"
        color="primary"
        @click="openImportDialog"
      >{{ $t('foodMenu.importMenu') }}</v-btn>
    </div>

    <div v-if="importStatuses.length > 0" class="my-4">
      <v-card
        class="pa-4"
        color="#f0f0f0"
        variant="outlined"
      >
        <div>{{ $t('foodMenu.importStatus') }}</div>
        <v-card
          color="#fff"
          class="my-2 pa-2"
          variant="flat"
          v-for="(status, i) in importStatuses"
          :key="i"
        >
          <div class="d-flex align-center">
            <div>
              {{ status.filename }}
            </div>
            <v-spacer />
            <div>
              <v-chip v-if="status.status === 'inProgress'" small color="orange" variant="outlined">
                {{ $t('foodMenu.reading') }}
              </v-chip>
              <v-chip v-else-if="status.status === 'failed'" small color="red" variant="outlined">
                {{ $t('foodMenu.readError') }}
              </v-chip>
              <v-chip v-else-if="status.status === 'success'" small color="primary" variant="outlined">
                {{ $t('foodMenu.readComplete') }}
              </v-chip>
            </div>
          </div>
        </v-card>
      </v-card>
    </div>

    <v-progress-linear v-if="isFetching" indeterminate class="my-4" color="primary" />

    <div class="menu-table">
      <div class="menu-table-headers">
        <div class="menu-table-header col-date">{{ $t('foodMenu.date') }}</div>
        <div class="menu-table-header col-category">{{ $t('foodMenu.category') }}</div>
        <div class="menu-table-header col-menu">{{ $t('foodMenu.menu') }}</div>
        <div class="menu-table-header col-classes">{{ $t('foodMenu.targetClasses') }}</div>
        <div class="menu-table-header col-actions"></div>
      </div>

      <div 
        class="menu-table-row"
        v-for="dwm in datesWithMenus"
        :key="dwm.date"
      >
        <div class="menu-table-row-col col-date">
          <div class="d-flex align-center">
            <div class="date-label"> {{ dwm.formatted }} </div>
            <v-spacer />
            <v-btn
              color="primary"
              variant="text"
              size="small"
              @click="setCreating(dwm.date)"
            >
              + {{ $t('common.add') }}
            </v-btn>
          </div>
        </div>
        <div class="menu-table-row-col-items">
          <div 
            class="menu-table-row-col-item"
            v-for="(menu, i) in dwm.menus"
            :key="i"
          >
            <div class="menu-table-row-col col-category">
              {{ menu.category.name }}
            </div>
            <div class="menu-table-row-col col-menu">
              {{ menu.body }}
            </div>
            <div class="menu-table-row-col col-classes">
              {{ formatClassNames(menu.category.targetClasses) }}
            </div>
            <div class="menu-table-row-col col-actions">
              <v-btn
                color="primary"
                variant="flat"
                rounded
                class="mx-1"
                small
                @click="openTestDialog(menu)"
              >
                <span v-if="menu.test">{{ $t('common_actions.inspected') }}</span>
                <span v-else>{{ $t('foodMenu.inspect') }}</span>
              </v-btn>
              <v-btn
                color="primary"
                variant="outlined"
                rounded
                class="mx-1"
                small
                @click="openEditDialog(menu)"
              >
                {{ $t('common.edit') }}
              </v-btn>
              <v-btn
                color="red"
                variant="outlined"
                rounded
                class="mx-1"
                small
                @click="deleteMenu(menu)"
              >
                {{ $t('common.delete') }}
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </div>

    <food-menu-create-container />
    <food-menu-test-container @delete:test="listMenus"/>
    <food-menu-edit-container />
    <food-menu-import-container />
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';

import dayjs from 'dayjs';
import 'dayjs/locale/en';
import { foodMenusModule } from '@/store/viewModules/food-menu/foodMenusModule';
import { MealPlanResponse, NurseryClassResponse } from 'chaild-api/lib';

import PageTitle from '@/components/atoms/PageTitle.vue';
import MonthPager from '@/components/atoms/MonthPager.vue';
import FoodMenuImportContainer from '@/containers/food-menu/FoodMenuImportContainer.vue';
import FoodMenuCreateContainer from '@/containers/food-menu/FoodMenuCreateContainer.vue';
import FoodMenuTestContainer from '@/containers/food-menu/FoodMenuTestContainer.vue';
import FoodMenuEditContainer from '@/containers/food-menu/FoodMenuEditContainer.vue';
import { foodMenuTestModule } from '@/store/viewModules/food-menu/foodMenuTestModule';
import LocalDataService from '@/service/LocalDataService';
import ApiResource from '@/api/ApiResource';

interface DateWithMenus {
  date: string;
  formatted: string;
  menus: MealPlanResponse[];
}

interface DataType {
  yearMonth: string;

  isFetching: boolean;
}

export default defineComponent({
  components: {
    PageTitle,
    MonthPager,
    FoodMenuCreateContainer,
    FoodMenuTestContainer,
    FoodMenuEditContainer,
    FoodMenuImportContainer,
  },

  data: (): DataType => ({
    yearMonth: dayjs().format('YYYY-MM'),
    
    isFetching: false,
  }),

  computed: {
    dates() {
      dayjs.locale('en');
      const date = dayjs(this.yearMonth).startOf('months');
      const daysInMonth = date.daysInMonth();
      return [...Array(daysInMonth)].map((emp, index) => {
        return date.add(index, 'day');
      });
    },
    testItems() {
      return [
        {
          category: 'Lunch (Baby Food)',
          menu: 'Rice, Miso Soup, Hijiki, Orange',
          classes: 'Class A, Class B, Class C, Class D',
        }
      ]
    },
    importStatuses() {
      return foodMenusModule.importStatuses;
    },
    menus() {
      return foodMenusModule.menus;
    },
    datesWithMenus(): DateWithMenus[] {
      return this.dates.map((date) => {
        const thisdayMenus = this.menus.filter((menu) => menu.date === date.format('YYYY-MM-DD'));
        return {
          date: date.format('YYYY-MM-DD'),
          formatted: date.locale('en').format('DD/MM (ddd)'),
          menus: thisdayMenus,
        };
      });
    },
    menuCategories() {
      return foodMenusModule.menuCategories;
    },
    allergens() {
      return foodMenusModule.allergens;
    },
    todayLabel() {
      return dayjs().locale('en').format('DD/MM (ddd)')
    },
    todayCode() {
      return dayjs().format('YYYY-MM-DD')
    },
    todaysMenu(): DateWithMenus | null {
      const menu = this.datesWithMenus.find((dwm) => dwm.date === dayjs().format('YYYY-MM-DD'));
      return menu || null;
    }
  },

  methods: {
    async pageClick(pageNumber) {
      if (pageNumber) sessionStorage.setItem('foodMenusYearMonth', String(pageNumber));
    },
    async listMenus() {
      this.isFetching = true;
      await foodMenusModule.listMenus({
        yearMonth: this.yearMonth
      });
      this.isFetching = false;
    },
    async listImportStatus() {
      const day = dayjs(this.yearMonth);
      let year = day.year();
      const month = day.month() + 1;

      if (month < 4) {
        year = year - 1;
      }

      foodMenusModule.listImportStatus({
        year,
        month,
      })
    },
    listMenuCategories() {
      foodMenusModule.listMealCategories();
    },
    listAllergens() {
      foodMenusModule.listAllergens();
    },
    setCreating(date: string) {
      foodMenusModule.setCreatingDate(dayjs(date).format('YYYY-MM-DD'));
    },
    formatClassNames(classes: NurseryClassResponse[]) {
      if (!classes) return '';
      return classes.map((c) => c.className).join(',');
    },
    openTestDialog(menu: MealPlanResponse) {
      foodMenuTestModule.setTestingMeal(menu);
    },
    openEditDialog(menu: MealPlanResponse) {
      foodMenusModule.setEditingMenu(menu);
    },
    openImportDialog() {
      foodMenusModule.setImportYearMonth(this.yearMonth);
      foodMenusModule.setIsImportDataDialogOpen(true);
    },
    async deleteMenu(menu: MealPlanResponse) {
      if (window.confirm(this.$t('foodMenu.deleteConfirm'))) {
        this.isFetching = true;
        await foodMenusModule.deleteMenu(menu.planId);
        await foodMenusModule.listMenus({ yearMonth: this.yearMonth });
        this.isFetching = false;
      }
    },
    async downloadSample() {
      const nurseryId = LocalDataService.getNurseryId();
      const filePath = 'meal/meal_plan_template.xlsx';

      if (!nurseryId) {
        return;
      }

      const response = await ApiResource.getDownloadUrl({
        nurseryId,
        path: filePath,
      });

      if (response?.preSignedDownloadUrl) {
        const link = document.createElement('a');
        link.href = response.preSignedDownloadUrl;
        link.download = 'meal_plan_template.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  },

  watch: {
    yearMonth: {
      handler: function() {
        this.listMenus();
        this.listImportStatus();
      },
    }
  },

  mounted() {
    const foodMenusYearMonth = sessionStorage.getItem('foodMenusYearMonth');
    if (foodMenusYearMonth) this.yearMonth = foodMenusYearMonth;
    this.listMenus();
    this.listImportStatus();
    this.listMenuCategories();
    this.listAllergens();
  }
});
</script>
<style lang="scss" scoped>
.menu-table-headers {
  display: flex;
  height: 30px;

  border-bottom: solid 1px #e0e0e0;
}

.menu-table-row {
  display: flex;

  border-bottom: solid 1px #e0e0e0;
}

.menu-table-header {
  font-size: 12px;
  font-weight: bold;
  line-height: 30px;
  
  padding: 0 8px;
}

.col-date {
  width: 160px;
}

.col-category {
  width: 160px;
}

.col-menu, .col-classes {
  flex: 1;
  min-width: 0;
}

.col-actions {
  flex: 0 0 auto;
  width: 320px;
  min-width: 320px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  box-sizing: border-box;
  flex-wrap: nowrap;
}

.menu-table-row-col {
  padding: 8px;
  padding-top: 12px;

  font-size: 14px;

  &.col-date {
    border-right: solid 1px #e0e0e0;
  }
}

.menu-table-row-col-items {
  flex: 1;
  min-width: 0;
}

.menu-table-row-col-item {
  display: flex;

  &:hover {
    background-color: #f0f0f0;
  }
}

.date-label {
  font-size: 14px;
}
</style>